import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import ProductLogo from 'account/ProductLogo'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import { AuthResponse, AuthenticationStatus, Feature, auth } from 'core'
import getLoginUser, { User } from 'core/application/account/loginUser'
import { ProductIds } from 'core/application/commons/config'
import store from 'core/application/commons/localStorage'

import { StyledInput as Input } from 'common/components/dataEntry/formik'

import useOverrideProduct from 'common/hooks/useOverrideProduct'
import useSwitchProduct from 'common/hooks/useSwitchProduct'
import useUrlQueryParam from 'common/hooks/useUrlQueryParam'

import {
  Button,
  Col,
  Content,
  DefaultCheckbox,
  Footer,
  Icon,
  Layout,
  Notification,
  PasswordInput,
  Row,
} from 'common/components'
import { useCommandRequestV2, useFeatureFlag, useQueryRequest } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'
import userPermissionAccess from 'common/userPermissionAccess'

import useLoginUser from '../../common/hooks/useLoginUser'
import AccountHeader from '../AccountHeader'
import RightSection from '../RightSection'

import '../AccountStyles.less'

export const LOGIN_PATH = '/login'

const formValidator = (translate: any) =>
  Yup.object({
    email: Yup.string()
      .email(translate('admin-login.errors.valid-email'))
      .required(translate('admin-login.errors.required-email')),
    password: Yup.string().required(translate('admin-login.errors.required-password')),
  })

interface FormValues {
  email: string
  password: string
  rememberMe: boolean
}

const initialValues: FormValues = {
  email: '',
  password: '',
  rememberMe: true,
}

const Login = () => {
  const [isLoading, setIsLoading] = useState(false)
  const { sendCommand: sendAuthCommand, isLoading: authLoading } = useCommandRequestV2<AuthResponse>(
    ({ email, password, rememberMe }) => auth(email, password, null, rememberMe),
  )
  const [, , sendGetLoginUser] = useQueryRequest(null)
  const [, setLoginUser] = useLoginUser()
  const productIdParam = useUrlQueryParam('productId')
  const productId = productIdParam ? Number(productIdParam) : ProductIds.GroupHraId
  const [step, setStep] = useState(0)
  const [userProducts, setUserProducts] = useState([])
  const [userCompanies, setUserCompanies] = useState([])
  const [hasAccessToInternalTools, setHasAccessToInternalTools] = useState(false)
  const [selectedUserCompany, setSelectedUserCompany] = useState<{ id: string; name: string } | null>(null)

  const signupPath = `/signup?productId=${productId}`

  const showSignUpDescription = useFeatureFlag(Feature.LoginSignUpDescription)

  const currentProduct = useOverrideProduct(productId)
  const { t: translate } = useTranslation()
  const history = useHistory()
  const headerItems: string[] = [
    translate('admin-login.subtitle-1'),
    translate('admin-login.subtitle-2'),
    translate('admin-login.subtitle-3'),
  ]

  const handleSwitch = (user: User) => {
    const {
      canAccessDashboard,
      canAccessAdminDashboard,
      canAccessEligibleEmployeeSignupWizard,
      canAccessNonEligibleEmployeeSignupWizard,
      canAccessNonEligibleEmployeeDashboard,
    } = userPermissionAccess(user)
    if (canAccessAdminDashboard) history.push('/admin-dashboard')
    else if (canAccessDashboard) history.push('/eligible-employee-dashboard')
    else if (canAccessNonEligibleEmployeeDashboard) history.push('/non-eligible-employee-dashboard')
    else if (canAccessEligibleEmployeeSignupWizard || canAccessNonEligibleEmployeeSignupWizard) history.push('/')
  }

  const { handleSwitchProduct } = useSwitchProduct(handleSwitch)

  const handleInternalToolsLogin = async () => {
    const user = await sendGetLoginUser(() => getLoginUser())
    if (user) {
      setLoginUser(user)
      store.setLastProductId(user.productId)
      history.push('/choose-app')
    }
  }

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)

    const result = await sendAuthCommand(values)

    if (!result.completed) {
      setIsLoading(false)
      return
    }

    if (!result.data.user) {
      setIsLoading(false)
      await Notification.error('You do not have an active company.')
    }

    if (result.data.status === AuthenticationStatus.PasswordResetRequired) {
      store.setTemporaryEmail(values.email)
      history.push(`${ROUTE_PATHS.PASSWORD_RESET_REQUIRED_PATH}?productId=${ProductIds.QsehraId}`)
      return
    }

    if (result.data.status === AuthenticationStatus.Ok) {
      setIsLoading(false)

      setHasAccessToInternalTools(result.data.user.hasAccessToInternalTools)
      const companies = result.data.user.companies
      const canAccessInternalTools = result.data.user.hasAccessToInternalTools
      let products = []
      companies.forEach((company) => {
        company.products.forEach((product) => {
          if (!products.map((p) => p.id).includes(product.id)) products = [...products, product]
        })
      })
      setUserCompanies(companies)
      store.setToken(result.data.token)
      if (companies.length === 1 && products.length === 1 && !canAccessInternalTools) {
        const user = await sendGetLoginUser(() => getLoginUser())
        if (user) {
          setLoginUser(user)
          store.setLastProductId(user.productId)
          history.push('/')
        }
      }

      if (companies.length > 1) {
        setStep(1)
      } else if (companies.length === 1 && (products.length > 1 || canAccessInternalTools)) {
        setSelectedUserCompany({ id: companies[0].id, name: companies[0].name })
        setUserProducts(companies[0].products)
        setStep(2)
      }
    }
  }

  const loginForm = (formik: FormikProps<FormValues>) => (
    <Row justify="center" align="middle">
      <div className="account-left-section-form">
        <Row align="middle" justify="center">
          <Col xs={24} md={24}>
            <div>
              <h3>{translate('admin-login.log-in')}</h3>
            </div>
          </Col>
          <Col xs={24} md={24} className="pt-24 pb-24">
            <Input size="default" label={translate('admin-login.labels.email')} name="email"></Input>
          </Col>
          <Col xs={24} md={24} className="pt-16 pb-16">
            <PasswordInput
              label={translate('admin-login.labels.password')}
              name="password"
              className="input-with-suffix__border-botton-only"
              setPassword={() => {}}
            ></PasswordInput>
          </Col>
          <Col xs={24} md={24} className="login--login-button pt-16 pb-16">
            <Button htmlType="submit" type="primary" className="button__large" loading={isLoading}>
              {translate('admin-login.log-in')}
            </Button>
          </Col>
          <Col xs={12} md={12}>
            <DefaultCheckbox name="rememberMe" className="no-select">
              Remember me
            </DefaultCheckbox>
          </Col>
          <Col xs={12} md={12}>
            <a
              onClick={() => {
                history.push(`${ROUTE_PATHS.FORGOT_PASSWORD_PATH}/${formik.values.email}?productId=${productId}`)
              }}
              className="signup--forgot-password"
            >
              {translate('admin-login.forgot-password')}
            </a>
          </Col>
          {showSignUpDescription && (
            <Row className="form__row flex__end w-100" justify="end">
              <Col className="text-end">
                <p>
                  If you're an employee logging in for the first time, use the invite link in your email to create your
                  account.
                </p>
                <p>
                  Are you an organization looking to set up health benefits?{' '}
                  <Button
                    type="link"
                    className="signup--login p-0"
                    href="https://www.peoplekeep.com/offers/get-started"
                  >
                    Sign Up
                  </Button>
                </p>
              </Col>
            </Row>
          )}
        </Row>
      </div>
    </Row>
  )

  const selectCompanyForm = () => (
    <Row justify="center" align="middle">
      <div className="account-left-section-form">
        <Row align="middle">
          <Col xs={24} md={24} className="mb-32">
            <div className="flex flex__align_center">
              <Button className="mr-8" size="default" htmlType="button" type="default" onClick={() => setStep(0)}>
                <Icon type="fa fa-arrow-left" fontAwesome />
              </Button>
              <div className="text-semi-bold font-16">Back to login</div>
            </div>
          </Col>
          <Col xs={24} md={24} className="mb-32">
            <div>
              <h3>{translate('admin-login.select-company')}</h3>
            </div>
          </Col>
          <div className="grid w-100">
            {userCompanies.map((company) => (
              <Button
                type="default"
                htmlType="button"
                className="button__large mb-16 flex flex_space_between flex__align_center pt-40 pb-40"
                disabled={authLoading}
                onClick={async () => {
                  if (company.products.length > 1) {
                    setStep(2)
                    setUserProducts(company.products)
                    setSelectedUserCompany(company)
                  } else {
                    await handleSwitchProduct(company.id, company.products[0].id)
                  }
                }}
              >
                <div className="text-left">
                  <div className="color-gray-8 text-semi-bold font-16">{company.name}</div>
                  <div className="color-gray-8 font-14">
                    {company.products.map((product, key) =>
                      key === company.products.length - 1 ? `${product.name}` : `${product.name},`,
                    )}
                  </div>
                </div>
                <Icon type="fa fa-arrow-right" fontAwesome />
              </Button>
            ))}
          </div>
        </Row>
      </div>
    </Row>
  )

  const productsDisplay = (products: { id: number }[], showInternalTools: boolean) =>
    showInternalTools ? [...products, { id: -1 }] : products

  const selectProductForm = () => (
    <Row justify="center" align="middle" data-testid={'login-options-wrapper'}>
      <div className="account-left-section-form">
        {selectedUserCompany && (
          <Row align="middle">
            <Col xs={24} md={24} className="mb-32">
              <div className="flex flex__align_center">
                <Button className="mr-8" size="default" htmlType="button" type="default" onClick={() => setStep(1)}>
                  <Icon type="fa fa-arrow-left" fontAwesome />
                </Button>
                <div className="text-semi-bold font-16">Back to company selection</div>
              </div>
            </Col>
            <Col xs={24} md={24} className="mb-32">
              <div>
                <h3>Welcome back, {selectedUserCompany.name}!</h3>
              </div>
            </Col>
            <div className="grid w-100">
              {productsDisplay(userProducts, hasAccessToInternalTools).map((product) => (
                <Button
                  type="default"
                  htmlType="button"
                  data-testid={`t-choose-product-${product.id}`}
                  className={`button__large mb-16 flex flex_space_between flex__align_center pt-40 pb-40 t-choose-product-${product.id}`}
                  onClick={async () => {
                    if (product.id === -1) await handleInternalToolsLogin()
                    else await handleSwitchProduct(selectedUserCompany.id, product.id)
                  }}
                >
                  <ProductLogo productId={String(product.id)} />
                  <Icon type="fa fa-arrow-right" fontAwesome />
                </Button>
              ))}
            </div>
          </Row>
        )}
      </div>
    </Row>
  )

  const stepperDisplay = (formik: FormikProps<FormValues>) => {
    switch (step) {
      case 0:
        return loginForm(formik)
      case 1:
        return selectCompanyForm()
      case 2:
        return selectProductForm()
      default:
        break
    }
  }

  return (
    <Layout>
      <Content style={{ height: '100vh' }}>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate)}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit} autoComplete="off" style={{ height: '100vh', overflow: 'auto' }}>
              <Row className="h-100">
                <Col xs={24} lg={16} className="flex justify_center">
                  <div className="account-left-section">
                    <div className="mt-64 mb-64">
                      <AccountHeader onlyMainLogo />
                    </div>
                    {stepperDisplay(formik)}
                  </div>
                </Col>
                <Col
                  xs={0}
                  lg={8}
                  className="account-right-section-signup account-right-section__fixed"
                  style={{ height: '100%' }}
                >
                  <RightSection headerTitle={translate(`${currentProduct?.signUpTitle}`)} headerItems={headerItems} />
                </Col>
              </Row>
            </form>
          )}
        </Formik>
      </Content>
      <Footer className="absolute" />
    </Layout>
  )
}

export default Login
